import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SummaryList, type SummaryRow } from '../../components/idsk';
import { formatDate } from '../../lib/format';
import { evaluate } from '../evaluate';
import { fallsBackToSlovak, text, type EvaluationContext, type FieldDef, type FormDefinition, type FormValues, type JsonValue } from '../types';
import { displayNumber } from './numbers';
import { ReferenceLabel } from './ReferenceField';
import type { Lang } from './FormRenderer';
import { isRichText } from '../richText';
import { RichTextView } from './RichTextView';
import { filledCells, isTableValue } from '../table';
import { TableView } from './TableInput';

/**
 * Read-only display of a status form as an ID-SK summary list (same engine: hidden fields are left out, disabled
 * fields say "Nevzťahuje sa").
 */
export function FormSummary({ definition, values, context, lang }: { definition: FormDefinition; values: FormValues; context: EvaluationContext; lang: Lang }) {
  const { t } = useTranslation();
  const result = useMemo(() => evaluate(definition, values, context), [definition, values, context]);

  const display = (f: FieldDef, raw: JsonValue | undefined): ReactNode => {
    // an active switch without a stored value is off (same as the engine)
    const v = f.type === 'SWITCH' && (raw === undefined || raw === null) ? false : raw;
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) return <span className="govuk-hint">{t('form.notFilled')}</span>;
    switch (f.type) {
      case 'NUMBER':
        return displayNumber(v, lang);
      case 'DATE':
        return typeof v === 'string' ? formatDate(v, lang) : String(v);
      case 'YES_NO':
        return v === true ? t('common.yes') : v === false ? t('common.no') : String(v);
      case 'SWITCH':
        return v === true ? t('form.switchOn') : v === false ? t('form.switchOff') : String(v);
      case 'AUTOCOMPLETE':
        return typeof v === 'string' ? <ReferenceLabel source={f.constraints?.source ?? 'SUBJECT'} id={v} /> : String(v);
      case 'SINGLE_CHOICE': {
        const o = f.options?.find((x) => x.value === v);
        return o ? <span lang={fallsBackToSlovak(o.label, lang) ? 'sk' : undefined}>{text(o.label, lang)}</span> : String(v);
      }
      case 'MULTI_CHOICE':
        return (
          <ul className="govuk-list">
            {(v as string[]).map((x) => {
              const o = f.options?.find((y) => y.value === x);
              return (
                <li key={x} lang={o && fallsBackToSlovak(o.label, lang) ? 'sk' : undefined}>
                  {o ? text(o.label, lang) : x}
                </li>
              );
            })}
          </ul>
        );
      case 'TEAM_MEMBER':
        return context.teamMembers?.find((m) => m.id === v)?.name ?? String(v);
      case 'TEXTAREA':
        return <span className="app-preserve-lines">{String(v)}</span>;
      case 'RICH_TEXT':
        return isRichText(v) ? <RichTextView value={v} /> : null;
      case 'TABLE':
        // a filled table is shown as a table of its own (see rowsFor)
        return <span className="govuk-hint">{t('form.notFilled')}</span>;
      default:
        return String(v);
    }
  };

  const rowsFor = (fields: FieldDef[], source: Record<string, JsonValue>, prefix: string): ReactNode[] => {
    const out: ReactNode[] = [];
    let pending: SummaryRow[] = [];
    const flush = (key: string) => {
      if (pending.length) out.push(<SummaryList key={`list-${key}`} rows={pending} />);
      pending = [];
    };
    for (const f of fields) {
      const path = `${prefix}${f.key}`;
      const state = result.state[path];
      if (!state?.visible || f.type === 'INFO') continue;
      const keyLang = fallsBackToSlovak(f.label, lang) ? 'sk' : undefined;
      if (f.type === 'SECTION') {
        flush(path);
        out.push(
          <section key={path} aria-labelledby={`summary-${path}`}>
            <h3 className="govuk-heading-s" id={`summary-${path}`} lang={keyLang}>
              {text(f.label, lang)}
            </h3>
            {rowsFor(f.children ?? [], source, prefix)}
          </section>,
        );
        continue;
      }
      if (f.type === 'REPEATING_GROUP') {
        flush(path);
        const rows = Array.isArray(source[f.key]) ? (source[f.key] as Record<string, JsonValue>[]) : [];
        out.push(
          <section key={path} aria-labelledby={`summary-${path}`}>
            <h3 className="govuk-heading-s" id={`summary-${path}`} lang={keyLang}>
              {text(f.label, lang)}
            </h3>
            {!state.enabled ? (
              <p className="govuk-body">{t('form.notApplicable')}</p>
            ) : rows.length === 0 ? (
              <p className="govuk-body">{t('form.notFilled')}</p>
            ) : (
              rows.map((row, i) => (
                <div key={i}>
                  <h4 className="govuk-heading-s govuk-!-font-weight-regular">{t('form.rowLegend', { label: text(f.label, lang), n: i + 1 })}</h4>
                  {rowsFor(f.children ?? [], row ?? {}, `${f.key}[${i}].`)}
                </div>
              ))
            )}
          </section>,
        );
        continue;
      }
      // a filled table: the table itself, after the rows before it
      const tableValue = source[f.key];
      if (f.type === 'TABLE' && state.enabled && isTableValue(f, tableValue) && filledCells(f, tableValue)) {
        flush(path);
        out.push(<TableView key={path} field={f} value={tableValue} lang={lang} />);
        continue;
      }
      pending.push({
        key: text(f.label, lang),
        keyLang,
        value: state.enabled ? display(f, source[f.key]) : <span>{t('form.notApplicable')}</span>,
      });
    }
    flush('end');
    return out;
  };

  return <div className="app-form-summary">{rowsFor(definition.fields, values, '')}</div>;
}
